"use client";

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import { supabase } from "../../supabaseClient";
import { formatProfileName } from "../../utils/profileName";

export default function CalendarPage() {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [learning, setLearning] = useState([]);
  const [selectedLearning, setSelectedLearning] = useState(null);
  const [competencies, setCompetencies] = useState([]);
  const [selectedCompetency, setSelectedCompetency] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchShifts();
      fetchAppointments();
      fetchLearning();
      fetchCompetencies();
    }
  }, [currentUser]);

  async function fetchUser() {
    const { data } = await supabase.auth.getUser();
    setCurrentUser(data.user);
  }

  async function fetchShifts() {
    const { data } = await supabase
      .from("shifts")
      .select("*")
      .order("time", { ascending: true });

    if (data) setShifts(data);
  }

  async function fetchAppointments() {
    const { data } = await supabase
      .from("appointments")
      .select(
        `
        id,
        patient_full_name,
        roleplay_name,
        department,
        appointment_datetime,
        location,
        status,
        clinician (id, full_name, name_prefix)
      `
      )
      .eq("clinician", currentUser.id)
      .order("appointment_datetime", { ascending: true });

    if (data) {
      const activeAppointments = data.filter((appt) => appt.status !== false);
      setAppointments(activeAppointments);
    }
  }

  async function fetchLearning() {
    if (!currentUser?.id) return;
    const { data, error } = await supabase
      .from("e_learning_assignments")
      .select(`
        id,
        due_at,
        assigned_at,
        revoked_at,
        module:e_learning_modules!e_learning_assignments_module_id_fkey(
          id, title, category, mandatory, estimated_minutes
        ),
        progress:e_learning_progress!e_learning_progress_assignment_id_fkey(
          status, completed_at, best_score
        )
      `)
      .eq("user_id", currentUser.id)
      .is("revoked_at", null)
      .not("due_at", "is", null)
      .order("due_at", { ascending: true });

    if (error) {
      console.warn("Unable to load E-Learning calendar entries:", error);
      setLearning([]);
      return;
    }

    setLearning(data || []);
  }

  async function fetchCompetencies() {
    if (!currentUser?.id) return;
    const { data, error } = await supabase
      .from("staff_competency_records")
      .select(`
        id,
        competency_id,
        achieved_on,
        expires_on,
        verified,
        certificate_reference,
        competency:staff_competency_catalog!staff_competency_records_competency_id_fkey(
          id, title, category, active
        )
      `)
      .eq("user_id", currentUser.id)
      .not("expires_on", "is", null)
      .order("expires_on", { ascending: true });

    if (error) {
      console.warn("Unable to load competency calendar entries:", error);
      setCompetencies([]);
      return;
    }

    setCompetencies((data || []).filter((item) => item.competency?.active !== false));
  }

  const startOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const endOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  );
  const startDay = startOfMonth.getDay();
  const daysInMonth = endOfMonth.getDate();

  const calendarDays = [];

  for (let i = 0; i < startDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++)
    calendarDays.push(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), d)
    );

  const eventsForDay = (day) => {
    if (!day) return [];
    const dayStr = day.toDateString();

    const dayShifts = shifts.filter(
      (s) => new Date(s.time).toDateString() === dayStr
    );
    const dayAppointments = appointments.filter(
      (a) => new Date(a.appointment_datetime).toDateString() === dayStr
    );
    const dayLearning = learning.filter(
      (item) => new Date(item.due_at).toDateString() === dayStr
    );
    const dayCompetencies = competencies.filter(
      (item) => new Date(`${item.expires_on}T12:00:00`).toDateString() === dayStr
    );

    return {
      shifts: dayShifts,
      appointments: dayAppointments,
      learning: dayLearning,
      competencies: dayCompetencies,
    };
  };

  const changeMonth = (offset) => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1)
    );
  };

  const formatDateTime = (dt) => {
    if (!dt) return "";
    const d = new Date(dt);
    return `${d.toLocaleDateString("en-GB")} ${d
      .toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      .replace(/ /, "")}`;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-1">
        <div className="nhsuk-width-container calendar-container">
          {/* Calendar Header */}
          <div className="calendar-header">
            <button onClick={() => changeMonth(-1)}>←</button>
            <h2 className="nhsuk-heading-m">
              {currentDate.toLocaleString("en-GB", {
                month: "long",
                year: "numeric",
              })}
            </h2>
            <button onClick={() => changeMonth(1)}>→</button>
          </div>

          {/* Calendar Grid */}
          <div className="calendar-grid">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="calendar-day-header">
                {d}
              </div>
            ))}

            {calendarDays.map((day, idx) => {
              const {
                shifts: dayShifts,
                appointments: dayAppointments,
                learning: dayLearning,
                competencies: dayCompetencies,
              } = eventsForDay(day) || {};
              return (
                <div key={idx} className="calendar-cell">
                  {day && (
                    <>
                      <div className="calendar-date">{day.getDate()}</div>

                      {dayShifts?.map((shift) => (
                        <button
                          key={shift.id}
                          className="calendar-event calendar-shift"
                          onClick={() => navigate(`/intranet/ecamis/shift/${shift.id}`)}
                        >
                          {shift.title}
                        </button>
                      ))}

                      {dayAppointments?.map((appt) => (
                        <button
                          key={appt.id}
                          className="calendar-event calendar-appointment"
                          onClick={() => setSelectedAppointment(appt)}
                        >
                          {appt.patient_full_name}
                        </button>
                      ))}

                      {dayLearning?.map((item) => (
                        <button
                          key={item.id}
                          className={`calendar-event calendar-learning${item.module?.mandatory ? " calendar-learning--mandatory" : ""}`}
                          onClick={() => setSelectedLearning(item)}
                        >
                          E-Learning: {item.module?.title || "Training"}
                        </button>
                      ))}

                      {dayCompetencies?.map((item) => (
                        <button
                          key={`competency-${item.id}`}
                          className="calendar-event calendar-competency"
                          onClick={() => setSelectedCompetency(item)}
                        >
                          Certification: {item.competency?.title || "Competency"}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />

      {/* Appointment Modal */}
      {selectedAppointment && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedAppointment(null)}
        >
          <div
            className="modal modal-appointment"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="nhsuk-heading-m">
              {selectedAppointment.patient_full_name}
            </h2>

            <div className="modal-row">
              <span className="label">Roleplay Name:</span>
              <span className="value">
                {selectedAppointment.roleplay_name || "–"}
              </span>
            </div>

            <div className="modal-row">
              <span className="label">Department:</span>
              <span className="value">{selectedAppointment.department}</span>
            </div>

            <div className="modal-row">
              <span className="label">Date & Time:</span>
              <span className="value">
                {formatDateTime(selectedAppointment.appointment_datetime)}
              </span>
            </div>

            <div className="modal-row">
              <span className="label">Location:</span>
              <span className="value">{selectedAppointment.location}</span>
            </div>

            <button
              className="nhsuk-button nhsuk-button--secondary modal-close"
              onClick={() => setSelectedAppointment(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {selectedLearning && (
        <div className="modal-overlay" onClick={() => setSelectedLearning(null)}>
          <div className="modal modal-learning" onClick={(e) => e.stopPropagation()}>
            <span className="pguh-eyebrow">E-Learning due date</span>
            <h2 className="nhsuk-heading-m">{selectedLearning.module?.title || "Staff E-Learning"}</h2>
            <div className="modal-row"><span className="label">Requirement:</span><span className="value">{selectedLearning.module?.mandatory ? "Mandatory" : "Development / non-mandatory"}</span></div>
            <div className="modal-row"><span className="label">Category:</span><span className="value">{selectedLearning.module?.category || "General"}</span></div>
            <div className="modal-row"><span className="label">Due:</span><span className="value">{formatDateTime(selectedLearning.due_at)}</span></div>
            <div className="modal-row"><span className="label">Status:</span><span className="value">{(Array.isArray(selectedLearning.progress) ? selectedLearning.progress[0]?.status : selectedLearning.progress?.status) || "Assigned"}</span></div>
            <Link className="nhsuk-button" to={`/intranet/e-learning?assignment=${selectedLearning.id}`}>Open E-Learning</Link>
            <button className="nhsuk-button nhsuk-button--secondary modal-close" onClick={() => setSelectedLearning(null)}>Close</button>
          </div>
        </div>
      )}

      {selectedCompetency && (
        <div className="modal-overlay" onClick={() => setSelectedCompetency(null)}>
          <div className="modal modal-competency" onClick={(e) => e.stopPropagation()}>
            <span className="pguh-eyebrow">Certification expiry</span>
            <h2 className="nhsuk-heading-m">{selectedCompetency.competency?.title || "Staff competency"}</h2>
            <div className="modal-row"><span className="label">Category:</span><span className="value">{selectedCompetency.competency?.category || "General"}</span></div>
            <div className="modal-row"><span className="label">Expires:</span><span className="value">{new Date(`${selectedCompetency.expires_on}T12:00:00`).toLocaleDateString("en-GB")}</span></div>
            <div className="modal-row"><span className="label">Verification:</span><span className="value">{selectedCompetency.verified ? "Verified" : "Awaiting verification"}</span></div>
            <div className="modal-row"><span className="label">Reference:</span><span className="value">{selectedCompetency.certificate_reference || "Not recorded"}</span></div>
            <Link className="nhsuk-button" to="/intranet/competencies">Open competencies</Link>
            <button className="nhsuk-button nhsuk-button--secondary modal-close" onClick={() => setSelectedCompetency(null)}>Close</button>
          </div>
        </div>
      )}

      <style>{`
        .calendar-container {
          padding-top: 3rem;
          padding-bottom: 4rem;
        }

        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .calendar-header button {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 0.5rem;
        }

        .calendar-day-header {
          font-weight: bold;
          text-align: center;
        }

        .calendar-cell {
          border: 1px solid #d8dde0;
          min-height: 110px;
          padding: 0.4rem;
        }

        .calendar-date {
          font-size: 0.8rem;
          margin-bottom: 0.3rem;
        }

        .calendar-event {
          display: block;
          width: 100%;
          border: none;
          border-radius: 4px;
          font-size: 0.75rem;
          padding: 0.25rem 0.4rem;
          margin-bottom: 0.25rem;
          cursor: pointer;
          text-align: left;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .calendar-shift {
          background: #005eb8;
          color: white;
        }

        .calendar-appointment {
          background: #007f3b;
          color: white;
        }

        /* MODAL */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
        }

        .modal {
          background: white;
          padding: 1.25rem;
          border-radius: 8px;
          max-width: 420px;
          width: 90%;
          display: flex;
          flex-direction: column;
        }

        .modal-shift {
          border-left: 5px solid #005eb8;
        }

        .modal-appointment {
          border-left: 5px solid #007f3b;
        }

        .modal-title {
          margin-bottom: 1rem;
        }

        .modal-row {
          display: flex;
          justify-content: space-between;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
          white-space: nowrap;
        }

        .label {
          font-weight: bold;
          flex-shrink: 0;
        }

        .value {
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .modal-close {
          margin-top: 1rem;
          width: 100%;
        }

        @media (max-width: 768px) {
          .calendar-cell {
            min-height: 90px;
          }

          .calendar-event {
            font-size: 0.7rem;
          }

          .calendar-container {
            padding-bottom: 3rem;
          }
        }

        .calendar-learning {
          background: #e8edee;
          border-left: 4px solid #005eb8;
          color: #003d78;
        }

        .calendar-learning--mandatory {
          background: #fff1f0;
          border-left-color: #d5281b;
          color: #7d1a13;
          font-weight: 700;
        }

        .modal-learning {
          border-left: 5px solid #005eb8;
        }

        .calendar-competency {
          background: #f3e5f5;
          border-left: 4px solid #7b1fa2;
          color: #4a145e;
          font-weight: 700;
        }

        .modal-competency {
          border-left: 5px solid #7b1fa2;
        }

        html[data-theme="dark"] .calendar-learning {
          background: #172734;
          color: #d8e9f7;
        }

        html[data-theme="dark"] .calendar-learning--mandatory {
          background: #321d1d;
          color: #ffd9d5;
        }


        html[data-theme="dark"] .calendar-competency {
          background: #2c2034;
          color: #f2d8ff;
        }
      `}</style>
    </div>
  );
}
